import { useCallback } from "react";

import { FormCategory, FormType } from "@simplyk/common";
import { OptionsObject, useSnackbar } from "notistack";

import { useTranslate } from "../../../../../common/hooks/useTranslate";

import { trpc } from "@/common/helpers/trpc";

type UseDeleteFormProps = {
  refetch?: () => Promise<unknown>;
};

export const useDeleteForm = ({ refetch }: UseDeleteFormProps = {}) => {
  const utils = trpc.useUtils();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const { mutateAsync: deleteDonationForm } = trpc.deleteDonationForm.useMutation();
  const { mutateAsync: deleteCampaign } = trpc.deleteCampaign.useMutation();
  const { mutateAsync: deleteTicketing } = trpc.deleteTicketing.useMutation();

  return useCallback(
    async ({
      id,
      formType,
      formCategory,
      onlyDraft = false,
    }: {
      id: string;
      formType: FormType;
      formCategory: FormCategory;
      onlyDraft?: boolean;
    }) => {
      const snackbarOptions: OptionsObject = {
        vibe: "positive",
        hideIcon: true,
      };
      const successi18nKey = "donationForm.deleteForm.success";
      if (formType === FormType.DonationForm) {
        if (formCategory === FormCategory.PeerToPeerCampaign) {
          const { data, error } = await deleteCampaign({ campaignId: id });
          if (error) {
            enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), {
              vibe: "danger",
            });
            return false;
          }
          if ((data ?? 0) > 1) {
            enqueueSnackbar(
              `${t("dashboard", "donationForm.deleteForms.success", {
                count: data,
              })}`,
              snackbarOptions
            );
          } else {
            enqueueSnackbar(t("dashboard", successi18nKey), snackbarOptions);
          }
        } else {
          const { error } = await deleteDonationForm({ donationFormId: id, onlyDraft });
          if (error) {
            enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), {
              vibe: "danger",
            });
            return false;
          }
          enqueueSnackbar(t("dashboard", successi18nKey), snackbarOptions);
        }
      } else {
        const { error } = await deleteTicketing({ ticketingId: id, onlyDraft });
        if (error) {
          enqueueSnackbar(t("common", "unknownErrorRetryOrContactUs"), {
            vibe: "danger",
          });
          return false;
        }
        enqueueSnackbar(t("dashboard", successi18nKey), { vibe: "positive", hideIcon: true });
      }
      await Promise.all([
        utils.getCampaignListPagedTableData.invalidate(),
        utils.getCampaignHubPageData.invalidate(undefined, { refetchType: "none" }),
        refetch?.(),
      ]);
      return true;
    },
    [deleteCampaign, deleteDonationForm, deleteTicketing, refetch, enqueueSnackbar, t, utils]
  );
};
